import format from 'date-fns/format'
import parse from 'date-fns/parse'

import analytics from '../analytics'
import analyticsCustomer from '../customer'
import { getAge } from './formatDates'

const isPresent = (value) => value !== undefined && value !== null && value !== ''

/**
 * Drop empty / undefined traits so partial identifies do not overwrite
 * existing Segment / Customer.io profile fields (e.g. login only has email).
 */
const compactTraits = (obj) => {
  const result = {}

  Object.entries(obj).forEach(([key, value]) => {
    if (!isPresent(value)) return

    if (typeof value === 'object' && !Array.isArray(value)) {
      const nested = compactTraits(value)
      if (Object.keys(nested).length > 0) result[key] = nested
      return
    }

    result[key] = value
  })

  return result
}

/**
 * Slash dates in this app are always dd/MM/yyyy (HeaderProvider, AccountDetail).
 * `new Date('25/08/1990')` is Invalid Date; `new Date('05/08/1990')` is May 8.
 */
const parseBirthdate = (birthdateRaw) => {
  if (typeof birthdateRaw === 'string' && birthdateRaw.includes('/')) {
    return parse(birthdateRaw, 'dd/MM/yyyy', new Date())
  }
  return new Date(birthdateRaw)
}

const ageFromBirthdate = (parsed) => {
  if (Number.isNaN(parsed.getTime())) return undefined
  const computedAge = getAge(parsed)
  return Number.isFinite(computedAge) ? computedAge : undefined
}

/**
 * Build traits for Segment + Customer.io identify from patient API data
 * and/or Auth0 user. Accepts either API field names (given_name) or
 * HeaderProvider userInfo keys (firstName).
 */
export const buildPatientIdentifyTraits = ({
  user,
  data = {},
  fum
} = {}) => {
  const firstName = data.given_name ?? data.firstName ?? data.name
  const lastName = data.family_name ?? data.lastName
  const phone = data.phone_number ?? data.phone
  const country = data.country
  const state = data.state
  const postalCode = data.zip_code ?? data.postalCode
  const objective = data.objective
  const birthdateRaw = data.birthdate

  let birthdate
  let age
  if (birthdateRaw) {
    try {
      const parsed = parseBirthdate(birthdateRaw)
      if (!Number.isNaN(parsed.getTime())) {
        birthdate = typeof birthdateRaw === 'string' && birthdateRaw.includes('/')
          ? birthdateRaw
          : format(parsed, 'dd/MM/yyyy')
        age = ageFromBirthdate(parsed)
      }
    } catch {
      // leave birthdate / age unset so we do not overwrite existing traits
    }
  }

  const traits = compactTraits({
    firstName,
    lastName,
    email: user?.email,
    phone,
    logins: user?.loginCount,
    address: {
      country,
      state,
      postalCode
    },
    objective,
    birthdate,
    age
  })

  if (fum) {
    try {
      traits.fum = format(new Date(fum), 'dd/MM/yyyy')
    } catch {
      // ignore invalid fum
    }
  }

  return traits
}

/**
 * Identify the patient in both Segment and Customer.io with the same traits.
 * No-ops when patientId is missing (e.g. before Auth0 metadata is ready).
 */
export const identifyPatient = (patientId, traits) => {
  if (!patientId) return
  analytics.identify(patientId, traits)
  analyticsCustomer.identify(patientId, traits)
}
